// pages/CoherenceVerbesPage.jsx
// Page admin pour vérifier la cohérence entre tables Mots et Verbes

import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckSquare, AlertTriangle } from 'lucide-react';
import './CoherenceVerbesPage.css';

const langues = [
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
  { code: 'es', flag: '🇪🇸', label: 'Espagnol' },
  { code: 'en', flag: '🇬🇧', label: 'Anglais' },
];

const flagForLang = (lang) => langues.find(l => l.code === lang)?.flag || lang;

export default function CoherenceVerbesPage() {
  const [motsOrphelins, setMotsOrphelins] = useState([]);
  const [verbesOrphelins, setVerbesOrphelins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMots, setSelectedMots] = useState([]);
  const [selectedVerbes, setSelectedVerbes] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resMots, resVerbes] = await Promise.all([
        fetch('/api/mots/verbes-orphelins'),
        fetch('/api/verbes/mots-orphelins'),
      ]);
      const dataMots = await resMots.json();
      const dataVerbes = await resVerbes.json();

      if (!dataMots.success) throw new Error(dataMots.error);
      if (!dataVerbes.success) throw new Error(dataVerbes.error);

      setMotsOrphelins(dataMots.mots);
      setVerbesOrphelins(dataVerbes.verbes);
      setSelectedMots([]);
      setSelectedVerbes([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const toggleMotSelection = (id) => {
    setSelectedMots(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleVerbeSelection = (id) => {
    setSelectedVerbes(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAllMots = () => {
    if (selectedMots.length === motsOrphelins.length) {
      setSelectedMots([]);
    } else {
      setSelectedMots(motsOrphelins.map(m => m.id));
    }
  };

  const selectAllVerbes = () => {
    if (selectedVerbes.length === verbesOrphelins.length) {
      setSelectedVerbes([]);
    } else {
      setSelectedVerbes(verbesOrphelins.map(v => v.id));
    }
  };

  const creerVerbesManquants = async () => {
    if (selectedMots.length === 0) return;
    setActionLoading(true);
    try {
      const res = await fetch('/api/mots/creer-verbes-manquants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motIds: selectedMots }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      alert(`${data.created} verbe(s) créé(s) dans la table Verbes`);
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const creerMotsManquants = async () => {
    if (selectedVerbes.length === 0) return;
    setActionLoading(true);
    try {
      const res = await fetch('/api/verbes/creer-mots-manquants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verbeIds: selectedVerbes }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      alert(`${data.created} mot(s) créé(s) dans la table Mots`);
      fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="coherence-verbes-page">
      <div className="page-header">
        <h1>Cohérence Mots / Verbes</h1>
        <button className="btn-refresh" onClick={fetchData} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
          Actualiser
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : (
        <>
          {/* Section 1 : Mots type=verbe sans entrée Verbes */}
          <div className="coherence-section">
            <div className="section-header">
              <h2>
                <AlertTriangle size={20} />
                Mots (type=verbe) sans entrée dans Verbes
                <span className="badge">{motsOrphelins.length}</span>
              </h2>
              {motsOrphelins.length > 0 && (
                <div className="section-actions">
                  <button className="btn-select-all" onClick={selectAllMots}>
                    <CheckSquare size={14} />
                    {selectedMots.length === motsOrphelins.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                  <button
                    className="btn-action"
                    onClick={creerVerbesManquants}
                    disabled={selectedMots.length === 0 || actionLoading}
                  >
                    Créer {selectedMots.length} verbe(s) manquant(s)
                  </button>
                </div>
              )}
            </div>

            {motsOrphelins.length === 0 ? (
              <div className="empty-state">Aucun orphelin trouvé</div>
            ) : (
              <div className="orphelins-list">
                {motsOrphelins.map(mot => (
                  <div key={mot.id} className="orphelin-item">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedMots.includes(mot.id)}
                        onChange={() => toggleMotSelection(mot.id)}
                      />
                      <span className="mot-info">
                        {flagForLang(mot.langue)} <strong>{mot.motSingulier}</strong>
                        {mot.contexte && <span className="contexte">({mot.contexte})</span>}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 2 : Verbes sans entrée Mots */}
          <div className="coherence-section">
            <div className="section-header">
              <h2>
                <AlertTriangle size={20} />
                Verbes sans entrée dans Mots (type=verbe)
                <span className="badge">{verbesOrphelins.length}</span>
              </h2>
              {verbesOrphelins.length > 0 && (
                <div className="section-actions">
                  <button className="btn-select-all" onClick={selectAllVerbes}>
                    <CheckSquare size={14} />
                    {selectedVerbes.length === verbesOrphelins.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                  <button
                    className="btn-action"
                    onClick={creerMotsManquants}
                    disabled={selectedVerbes.length === 0 || actionLoading}
                  >
                    Créer {selectedVerbes.length} mot(s) manquant(s)
                  </button>
                </div>
              )}
            </div>

            {verbesOrphelins.length === 0 ? (
              <div className="empty-state">Aucun orphelin trouvé</div>
            ) : (
              <div className="orphelins-list">
                {verbesOrphelins.map(verbe => (
                  <div key={verbe.id} className="orphelin-item">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedVerbes.includes(verbe.id)}
                        onChange={() => toggleVerbeSelection(verbe.id)}
                      />
                      <span className="mot-info">
                        {flagForLang(verbe.langue)} <strong>{verbe.infinitif}</strong>
                        {verbe.isEnriched && <span className="enrichi-badge">enrichi</span>}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
